<template>
  <div class="sp-wrapper">
    <div class="sp-labels">
      <template v-for="(group, gi) in groups" :key="'label-' + gi">
        <div v-if="gi > 0" class="sp-label-spacer" />
        <span
          v-if="group.label || group.icon"
          class="sp-label"
          :style="labelStyle(gi)"
        >
          <svg v-if="group.icon === 'timeline'" class="sp-label-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M2 8h10M9 5l3 3-3 3" />
          </svg>
          <svg v-else-if="group.icon === 'cycle'" class="sp-label-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12.5 6.5A4.5 4.5 0 0 0 4 5.5M3.5 9.5A4.5 4.5 0 0 0 12 10.5" />
            <path d="M4 3v3H1M12 13v-3h3" />
          </svg>
          <span v-if="group.label">{{ group.label }}</span>
        </span>
      </template>
    </div>
    <div class="sp-track" ref="trackEl">
      <!-- Sliding indicator -->
      <div class="sp-indicator" :style="indicatorStyle" />
      <template v-for="(group, gi) in groups" :key="gi">
        <!-- Divider between groups -->
        <div v-if="gi > 0" class="sp-divider" />
        <button
          v-for="(opt, oi) in group.options"
          :key="opt.value"
          :ref="(el) => setRef(gi, oi, el as HTMLElement | null)"
          class="sp-option"
          :class="{ 'sp-option--active': modelValue === opt.value }"
          @click="$emit('update:modelValue', opt.value)"
        >
          {{ opt.label }}
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, nextTick, watch, computed } from 'vue';

export interface PickerOption {
  value: string;
  label: string;
}

export interface PickerGroup {
  label?: string;
  icon?: 'timeline' | 'cycle';
  options: PickerOption[];
}

const props = defineProps<{
  modelValue: string;
  groups: PickerGroup[];
}>();

defineEmits<{
  'update:modelValue': [value: string];
}>();

const trackEl = ref<HTMLElement>();
const buttonRefs = reactive<Record<string, HTMLElement | null>>({});
const tick = ref(0);

function setRef(gi: number, oi: number, el: HTMLElement | null) {
  buttonRefs[`${gi}-${oi}`] = el;
}

onMounted(() => {
  nextTick(() => { tick.value++; });
});

watch(() => props.modelValue, () => {
  nextTick(() => { tick.value++; });
});

function labelStyle(groupIndex: number): Record<string, string> {
  // eslint-disable-next-line no-unused-expressions
  tick.value;

  const group = props.groups[groupIndex];
  if (!group || !group.options.length || !trackEl.value) return {};

  const track = trackEl.value;
  const trackRect = track.getBoundingClientRect();

  const firstBtn = buttonRefs[`${groupIndex}-0`];
  const lastBtn = buttonRefs[`${groupIndex}-${group.options.length - 1}`];
  if (!firstBtn || !lastBtn) return {};

  const firstRect = firstBtn.getBoundingClientRect();
  const lastRect = lastBtn.getBoundingClientRect();

  const left = firstRect.left - trackRect.left;
  const width = lastRect.right - firstRect.left;

  return {
    left: `${left}px`,
    width: `${width}px`,
  };
}

const indicatorStyle = computed(() => {
  // eslint-disable-next-line no-unused-expressions
  tick.value;

  for (let gi = 0; gi < props.groups.length; gi++) {
    const group = props.groups[gi];
    const oi = group.options.findIndex((o) => o.value === props.modelValue);
    if (oi === -1) continue;

    const btn = buttonRefs[`${gi}-${oi}`];
    const track = trackEl.value;
    if (!btn || !track) return { opacity: '0' };

    const trackRect = track.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();

    return {
      left: `${btnRect.left - trackRect.left}px`,
      width: `${btnRect.width}px`,
      opacity: '1',
    };
  }
  return { opacity: '0' };
});
</script>

<style scoped>
.sp-wrapper {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
}

.sp-labels {
  position: relative;
  display: flex;
  height: 13px;
}

.sp-label {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  font-family: 'Lato', sans-serif;
  font-size: 8.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(107, 114, 128, 0.6);
  white-space: nowrap;
}

:is(.dark) .sp-label {
  color: rgba(255, 255, 255, 0.35);
}

.sp-label-icon {
  width: 11px;
  height: 11px;
  flex-shrink: 0;
}

.sp-label-spacer {
  width: 5px;
  flex-shrink: 0;
}

.sp-track {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 3px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.04);
}

:is(.dark) .sp-track {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.06);
}

.sp-divider {
  width: 1px;
  height: 14px;
  background: rgba(0, 0, 0, 0.12);
  margin: 0 2px;
  flex-shrink: 0;
}

:is(.dark) .sp-divider {
  background: rgba(255, 255, 255, 0.12);
}

.sp-indicator {
  position: absolute;
  top: 3px;
  bottom: 3px;
  border-radius: 7px;
  background: #06C8FF;
  box-shadow: 0 1px 4px rgba(6, 200, 255, 0.3);
  transition: left 0.25s cubic-bezier(0.4, 0, 0.2, 1),
              width 0.25s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.15s ease;
  z-index: 0;
}

:is(.dark) .sp-indicator {
  box-shadow: 0 1px 8px rgba(6, 200, 255, 0.25), 0 0 0 0.5px rgba(6, 200, 255, 0.2);
}

.sp-option {
  position: relative;
  z-index: 1;
  padding: 4px 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: 'Lato', sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(55, 65, 81, 0.6);
  border-radius: 7px;
  transition: color 0.2s ease;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  line-height: 1.2;
}

:is(.dark) .sp-option {
  color: rgba(255, 255, 255, 0.45);
}

.sp-option--active {
  color: #fff;
  text-shadow: 0 0.5px 1px rgba(0, 0, 0, 0.15);
}

:is(.dark) .sp-option--active {
  color: #fff;
}

.sp-option:not(.sp-option--active):active {
  background: rgba(0, 0, 0, 0.04);
}

:is(.dark) .sp-option:not(.sp-option--active):active {
  background: rgba(255, 255, 255, 0.05);
}
</style>
